<template>
  <div class="expert-category-select">
    <a-select 
      size="large" 
      mode="multiple"  
      placeholder="请选择专业特长" 
      :value="selectedValues" 
      @change="handleChange"
      style="width: 100%" 
      show-search
      :filter-option="false"
      @search="handleSearch"
      @blur="handleBlur"
    >
      
      <!-- 扁平化选项用于回显 -->
      <a-select-option v-for="opt in allOptions" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </a-select-option>

      <!-- 自定义下拉内容 -->
      <div slot="dropdownRender" slot-scope="menu">
        <div class="custom-dropdown-content" @mousedown.prevent>
          <div v-if="filteredGroups.length === 0" class="no-data">
            暂无数据
          </div>
          <div v-for="group in filteredGroups" :key="group.key" class="custom-group">
            <div class="group-title" @click="toggleGroup(group.key)">
              <span class="expand-icon">
                <a-icon :type="(searchText || expandedGroups.includes(group.key)) ? 'down' : 'right'" />
              </span>
              <span v-html="highlightText(group.name)"></span>
            </div>
            <div class="group-content" v-show="searchText || expandedGroups.includes(group.key)">
              <div v-for="subGroup in group.childs" :key="subGroup.key" class="sub-group">
                <div class="sub-group-title" @click.stop="toggleSubGroup(subGroup.key)">
                  <span class="expand-icon">
                    <a-icon :type="(searchText || expandedSubGroups.includes(subGroup.key)) ? 'down' : 'right'" />
                  </span>
                  <span v-html="highlightText(subGroup.name)"></span>
                </div>
                <div class="sub-group-options" v-show="searchText || expandedSubGroups.includes(subGroup.key)">
                  <div 
                    v-for="opt in subGroup.options" 
                    :key="opt.value" 
                    class="custom-option-item"
                    :class="{
                      'selected': selectedValues.includes(opt.value)
                    }"
                    @click="handleOptionClick(group.key, opt)"
                  >
                    <div class="option-header">
                      <span class="option-label" v-html="highlightText(opt.label)"></span>
                      <a-icon v-if="selectedValues.includes(opt.value)" type="check" class="check-icon" />
                    </div>
                    <div class="option-desc" v-if="opt.description">
                      {{ opt.description }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-select>
    <div class="helper-text" v-if="selectionMode === 'multiple' && (min > 0 || max > 0)">
      <span v-if="min > 0">最少选{{min}}个类别</span>
      <span v-if="min > 0 && max > 0">，</span>
      <span v-if="max > 0">最多选{{max}}个类别</span>
    </div>
  </div>
</template>

<script>
import { getExpertCategorys } from "@/api/common.js";

export default {
  name: "ExpertCategorySelect",
  props: {
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectionMode: {
      type: String,
      default: 'multiple',
      validator: value => ['single', 'multiple'].includes(value)
    },
    max: {
      type: [Number, String],
      default: 0 // 0 means no limit
    },
    min: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      groups: [],
      expandedGroups: [], // 存储已展开的组 key
      expandedSubGroups: [], // 存储已展开的二级组 key
      searchText: '',
    };
  },
  computed: {
    // 过滤后的分组数据
    filteredGroups() {
      if (!this.searchText) {
        return this.groups;
      }
      const keyword = this.searchText.toLowerCase();
      
      // 深度拷贝并过滤
      const result = [];
      this.groups.forEach(group => {
        // 检查 L1 是否匹配
        const isL1Match = group.name.toLowerCase().includes(keyword);
        
        let hasMatchingChild = false;
        const filteredChilds = [];
        
        if (group.childs) {
          group.childs.forEach(subGroup => {
            // 检查 L2 是否匹配
            const isL2Match = subGroup.name.toLowerCase().includes(keyword);
            
            let hasMatchingOption = false;
            const filteredOptions = [];
            
            if (subGroup.options) {
              subGroup.options.forEach(opt => {
                // 检查 L3 是否匹配
                const isL3Match = opt.label.toLowerCase().includes(keyword);
                
                // 如果 L3 匹配，或者 L2 匹配，或者 L1 匹配，则保留该选项
                // 注意：如果父级匹配，通常显示所有子级，但为了过滤效果，这里可以只显示匹配路径
                // 根据需求：输入关键字筛选过滤对应的1级 2级 3级
                // 策略：只要有一级匹配，就保留整个路径。
                // 但如果只是 L1 匹配，是否显示所有 L2/L3？通常是的。
                // 如果只是 L3 匹配，只显示该 L3。
                
                if (isL3Match || isL2Match || isL1Match) {
                   filteredOptions.push(opt);
                   hasMatchingOption = true;
                }
              });
            }
            
            // 如果 L2 匹配，或者有匹配的 L3，或者 L1 匹配
            if (isL2Match || hasMatchingOption || isL1Match) {
              filteredChilds.push({
                ...subGroup,
                options: filteredOptions
              });
              hasMatchingChild = true;
            }
          });
        }
        
        // 如果 L1 匹配，或者有匹配的子级
        if (isL1Match || hasMatchingChild) {
          result.push({
            ...group,
            childs: filteredChilds
          });
        }
      });
      return result;
    },
    // Flatten the object into a single array for the select component
    selectedValues() {
      if (!Array.isArray(this.value)) {
        return []
      }
      return this.value.map(item => item.id)
    },
    // 所有选项扁平化，供 a-select-option 使用
    allOptions() {
      const options = [];
      this.groups.forEach(group => {
        if (group.childs) {
          group.childs.forEach(subGroup => {
            if (subGroup.options) {
              subGroup.options.forEach(opt => {
                options.push({
                  ...opt,
                  label: `${group.name} - ${subGroup.name} - ${opt.label}`
                });
              });
            }
          });
        }
      });
      return options;
    }
  },
  created() {
    this.loadExpertCategorys();
  },
  methods: {
    handleSearch(value) {
      this.searchText = value;
    },
    handleBlur() {
      // 可选：失去焦点时是否清空搜索？
      // this.searchText = '';
    },
    highlightText(text) {
      if (!this.searchText) return text;
      const regex = new RegExp(`(${this.searchText})`, 'gi');
      return text.replace(regex, '<span style="color: #f50; font-weight: bold;">$1</span>');
    },
    loadExpertCategorys() {
      getExpertCategorys().then(res => {
        if (res && res.length > 0) {
          this.groups = res.map(group => ({
            key: group.id,
            name: group.categoryName,
            childs: (group.childs || []).map(child => ({
              key: child.id,
              name: child.categoryName,
              parentId: child.parentId,
              options: (child.childs || []).map(grandChild => ({
                label: grandChild.categoryName,
                value: grandChild.id,
                parentId: grandChild.parentId,
                description: grandChild.des || '',
                // Preserve hierarchy info for selection
                level1Id: group.id,
                level1Name: group.categoryName,
                level2Id: child.id,
                level2Name: child.categoryName
              }))
            }))
          }));
          if (this.groups.length > 0) {
            this.expandedGroups = [this.groups[0].key];
          }
        }
      });
    },
    toggleGroup(key) {
      const index = this.expandedGroups.indexOf(key);
      if (index > -1) {
        this.expandedGroups.splice(index, 1);
      } else {
        this.expandedGroups.push(key);
      }
    },
    toggleSubGroup(key) {
      const index = this.expandedSubGroups.indexOf(key);
      if (index > -1) {
        this.expandedSubGroups.splice(index, 1);
      } else {
        this.expandedSubGroups.push(key);
      }
    },
    handleOptionClick(groupKey, opt) {
      const value = opt.value;
      const index = this.selectedValues.indexOf(value);
      let newValues = [];
      
      if (this.selectionMode === 'single') {
        newValues = [value];
      } else {
        newValues = [...this.selectedValues];
        if (index > -1) {
          newValues.splice(index, 1);
        } else {
          // Check Max Level 2 Categories Limit
          if (this.max > 0) {
            const currentLevel2Ids = new Set();
            // Find Level 2 IDs for currently selected items
            this.value.forEach(item => {
              if (item && item.parentId) {
                currentLevel2Ids.add(item.parentId);
              } else {
                const id = item.id || item;
                const option = this.allOptions.find(o => o.value === id);
                if (option && option.level2Id) {
                  currentLevel2Ids.add(option.level2Id);
                }
              }
            });

            const newLevel2Id = opt.level2Id;
            // Check if this new item adds a new Level 2 Category
            if (newLevel2Id && !currentLevel2Ids.has(newLevel2Id)) {
              if (currentLevel2Ids.size >= this.max) {
                this.$message.warning(`最多只能选择${this.max}个二级类别`);
                return;
              }
            }
          }
          newValues.push(value);
        }
      }
      
      this.handleChange(newValues);
    },
    handleChange(values) {
      // Clear search text on selection if desired, or keep it.
      // Usually users might want to select multiple items from the same search result.
      // So we keep it. But a-select might clear its input value.
      // Check if we need to sync searchText. 
      // Ant Design Vue Select with mode="multiple" clears input on select.
      // So we should probably clear searchText too to match UI.
      this.searchText = '';
      
      const result = []
      values.forEach(val => {
        let found = false;
        for (const group of this.groups) {
          if (group.childs) {
            for (const subGroup of group.childs) {
              if (subGroup.options) {
                const option = subGroup.options.find(opt => opt.value === val);
                if (option) {
                  result.push({
                    id: option.value,
                    parentId: option.parentId,
                    name: option.label,
                    categoryName: option.label,
                    description: option.description,
                    parentName: subGroup.name, // L2 Name
                    level1Id: option.level1Id,
                    level1Name: option.level1Name,
                    level2Id: option.level2Id,
                    level2Name: option.level2Name
                  });
                  found = true;
                  break;
                }
              }
            }
          }
          if (found) break;
        }
      });
      this.$emit("input", result);
      this.$emit("change", result);
    },
  },
};
</script>
<style lang="less" scoped>
.expert-category-select {
  width: 100%;
}
.helper-text {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
}
.custom-dropdown-content {
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;
}
.custom-group {
  padding: 0 12px;
  margin-bottom: 8px;
  
  .group-title {
    color: #333;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
    padding: 8px;
    background-color: #fafafa;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: #f0f0f0;
    }
    
    .expand-icon {
      margin-right: 8px;
      font-size: 12px;
      color: #999;
      width: 16px;
      text-align: center;
    }
  }
  
  .group-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.sub-group {
  margin-top: 4px;
  margin-bottom: 4px;
  padding-left: 8px;
  border-left: 2px solid #f5f5f5;
}

.sub-group-title {
  font-size: 13px;
  color: #555;
  font-weight: 500;
  margin-bottom: 6px;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 4px;
  
  &:hover {
    background-color: #f5f5f5;
  }

  .expand-icon {
    margin-right: 6px;
    font-size: 12px;
    color: #999;
    width: 14px;
    text-align: center;
  }
}

.sub-group-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 24px;
}

.custom-option-item {
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #f5f5f5;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  
  &:hover {
    background-color: #e6f7ff;
    
    .option-header {
      color: #1890ff;
    }
  }
  
  &.selected {
    background-color: #e6f7ff;
    
    .option-header {
      color: #1890ff;
      font-weight: 500;
    }
  }
  
  &.disabled {
    color: #00000040;
    background-color: #f5f5f5;
    cursor: not-allowed;
    
    .option-header {
       color: #00000040;
    }
    
    &:hover {
      color: #00000040;
      background-color: #f5f5f5;
    }
  }
  
  .option-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .check-icon {
    font-size: 12px;
  }
  
  .option-desc {
    font-size: 12px;
    color: #999;
    line-height: 1.5;
    word-break: break-all;
    white-space: pre-wrap;
  }
}
.no-data {
  padding: 12px;
  text-align: center;
  color: #999;
}
</style>
